/**
 * Function to calculate the overlap percentage of 'wgt' between tickers that exist in multiple parents (ETFs).
 *
 * @param {Array} data - The flat array of objects containing 'parent', 'ticker', and 'wgt' fields.
 * @returns {Array} - Array of objects representing the overlap percentage between pairs of parents.
 *                    Each object contains 'x' (parent1), 'y' (parent2), and 'value' (overlap percentage).
 */
function calculateOverlap(data) {
    // Step 1: Create a map to store tickers with their corresponding parents and weights
    const tickerMap = new Map();

    data.forEach(item => {
        if (!tickerMap.has(item.ticker)) {
            tickerMap.set(item.ticker, []);
        }
        tickerMap.get(item.ticker).push({ parent: item.parent, wgt: item.wgt });
    });

    // Step 2: Calculate overlap percentage for each pair of parents
    const overlapResults = [];
    const parentPairs = new Map();

    tickerMap.forEach((parentWgts, ticker) => {
        for (let i = 0; i < parentWgts.length; i++) {
            for (let j = i + 1; j < parentWgts.length; j++) {
                const parent1 = parentWgts[i].parent;
                const parent2 = parentWgts[j].parent;
                const wgt1 = parentWgts[i].wgt;
                const wgt2 = parentWgts[j].wgt;
                const pairKey = `${parent1}-${parent2}`;

                if (!parentPairs.has(pairKey)) {
                    parentPairs.set(pairKey, { totalWgt1: 0, totalWgt2: 0, overlapWgt: 0 });
                }

                const pairData = parentPairs.get(pairKey);
                pairData.totalWgt1 += wgt1;
                pairData.totalWgt2 += wgt2;
                pairData.overlapWgt += Math.min(wgt1, wgt2);
            }
        }
    });

    parentPairs.forEach((pairData, pairKey) => {
        const [parent1, parent2] = pairKey.split('-');
        const overlapPercentage = (pairData.overlapWgt / Math.min(pairData.totalWgt1, pairData.totalWgt2)) * 100;
        overlapResults.push({ x: parent1, y: parent2, value: overlapPercentage });
    });

    return overlapResults;
}


function flattenEtfPositions(etfPositions) {
  const etfWeight = 1 / etfPositions.length;

  return etfPositions.reduce((acc, etf) => {
    etf.holdings.forEach(holding => {
      acc.push({
        parent: etf.symbol,
        ...holding,
        symbol: holding.symbol,
        wgt: holding.wgt,
        scaledWeight: holding.wgt * etfWeight,
      });
    });
    return acc;
  }, []);
}

function countUniqueHoldings(data) {
    // Use a Set to store unique tickers
    const uniqueTickers = new Set();

    data.forEach(item => {
        item?.holdings?.forEach(x=>{
            uniqueTickers.add(x.symbol);
        })
    });

    // Return the size of the Set as the count of unique holdings
    return uniqueTickers.size;
}


export { calculateOverlap, flattenEtfPositions, countUniqueHoldings };